// Customer self-service account models (customer portal — /auth/account).
// Backed by issues #1017 (profile read/update) and #1019 (notification prefs).

/** Biological sex options offered on the account form (matches the SEX enum domain). */
export type CustomerAccountSex = 'MALE' | 'FEMALE' | 'PREFER_NOT_TO_SAY';

/** Preferred weight unit for dashboard rendering (#817 Tier 1, matches the Prisma `DisplayUnit` enum). */
export type CustomerDisplayUnit = 'LBS' | 'KG';

/** Editable profile fields a customer manages from the account page (#949). */
export interface CustomerAccountProfileI {
  firstName: string;
  lastName: string;
  dob: string; // ISO date (yyyy-MM-dd) — bound to <input type="date">
  sex: CustomerAccountSex;
  emailAddress: string;
  phoneNumber: string; // optional in the UI
  homeGym: string; // optional in the UI — gym / affiliation
  emergencyName: string;
  emergencyPhone: string; // optional in the UI
  // Preferred weight unit for the dashboard (#817 Tier 1). The backend always
  // returns this (NOT NULL DEFAULT 'LBS'); kept optional here so existing
  // profile literals/consumers compile unchanged until the dashboard renders it.
  displayUnit?: CustomerDisplayUnit;
}

/**
 * PATCH body for the customer self-service profile endpoint (#1017).
 *
 * Mirrors the backend `UpdateSelfProfileDto` whitelist exactly: `emailAddress`
 * is the read-only identity field (resolved server-side from the JWT) and is
 * NOT accepted on the wire — the route's `whitelist` + `forbidNonWhitelisted`
 * ValidationPipe would reject it with HTTP 400. Keep this in lockstep with the
 * DTO so the editable field set has a single shared definition.
 */
export type CustomerProfileUpdatePayloadI = Omit<CustomerAccountProfileI, 'emailAddress'>;

/**
 * POST body for the customer self-service profile CREATE endpoint (#1760 —
 * the onboarding step that creates the Customer row after Auth0 signup).
 *
 * Mirrors the backend `CreateSelfProfileDto` whitelist exactly. As with the
 * PATCH payload, `emailAddress` is deliberately absent: identity is resolved
 * server-side from the JWT, and a smuggled email is rejected with HTTP 400 by
 * the route's `whitelist` + `forbidNonWhitelisted` ValidationPipe.
 */
export interface CustomerProfileCreatePayloadI {
  firstName: string;
  lastName: string;
  sex: CustomerAccountSex;
  birthDate: string; // ISO date (yyyy-MM-dd) — bound to <input type="date">
  phoneNumber: string;
  gender?: string;
  ethnicity?: string;
  race?: string;
}

/** Read-only summary stats rendered in the avatar row (sourced from client-history). */
export interface CustomerAccountSummaryI {
  testsCount: number;
  bodyFatPctChange: number | null; // null when fewer than two data points
  leanMassChangeLbs: number | null;
}

/** Notification preference toggles (#953). */
export interface CustomerNotificationPreferencesI {
  results: boolean;
  reminders: boolean;
  offers: boolean;
  newsletter: boolean;
}

/** Full data-subject export (#1020, ADR-0014 §3, access right) — own data only. */
export interface CustomerDataExportI {
  exportedAt: string;
  profile: CustomerAccountProfileI;
  measurements: Array<{
    id: string;
    testDate: string;
    fatPercent: string | null;
    leanLbs: number | null;
    waterLbs: number | null;
    restingMetabolicRate: number | null;
  }>;
  bookings: Array<{
    id: string;
    status: string;
    bookedAt: string;
    isPaid: boolean;
    isCanceled: boolean;
  }>;
  orders: Array<{
    id: string;
    orderNumber: string;
    orderDate: string | null;
    status: string;
  }>;
}
